import React, { useState, useRef } from 'react';
import { View, Text, TouchableOpacity, Modal, FlatList, TextInput as RNTextInput, Platform } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { sdStyles } from './NhoAddOverlay.styles';

export interface SDOption {
  id: string;
  displayText: string;
  [key: string]: any;
}

export interface SearchableDropdownProps {
  label?: string;
  options: SDOption[];
  selected: SDOption | null;
  onChange: (selected: SDOption | null) => void;
  placeholder?: string;
  /** Capacity hint — rendered items capped at this value (default 50) */
  maxItems?: number;
}

const NhoSearchableDropdown: React.FC<SearchableDropdownProps> = ({
  label,
  options,
  selected,
  onChange,
  placeholder = 'Select...',
  maxItems = 50,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [menuStyle, setMenuStyle] = useState({ top: 0, left: 0, width: 200 });
  const triggerRef = useRef<any>(null);

  const toggle = () => {
    if (!open && triggerRef.current) {
      if (Platform.OS === 'web') {
        try {
          const rect = (triggerRef.current as any).getBoundingClientRect();
          const scrollY = typeof window !== 'undefined' ? window.scrollY || 0 : 0;
          setMenuStyle({ top: rect.bottom + scrollY, left: rect.left, width: rect.width });
        } catch (_) { /* ignore */ }
      } else {
        triggerRef.current.measureInWindow?.((x: number, y: number, w: number, h: number) => {
          setMenuStyle({ top: y + h, left: x, width: w });
        });
      }
    }
    setOpen((o) => !o);
  };

  const filtered = options
    .filter((o) => o.displayText.toLowerCase().includes(search.toLowerCase()))
    .slice(0, maxItems);

  const handleSelect = (item: SDOption) => {
    onChange(item);
    setSearch('');
    setOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setSearch('');
  };

  return (
    <View style={sdStyles.container}>
      {label ? <Text style={sdStyles.label}>{label}</Text> : null}
      <TouchableOpacity ref={triggerRef} style={sdStyles.trigger} onPress={toggle} activeOpacity={0.8}>
        <Text style={[sdStyles.triggerText, !selected && sdStyles.placeholder]} numberOfLines={1}>
          {selected ? selected.displayText : placeholder}
        </Text>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          {selected && (
            <TouchableOpacity onPress={handleClear} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <MaterialIcons name="close" size={16} color="#888" style={{ marginRight: 4 }} />
            </TouchableOpacity>
          )}
          <MaterialIcons name={open ? 'arrow-drop-up' : 'arrow-drop-down'} size={20} color="#495057" />
        </View>
      </TouchableOpacity>

      <Modal visible={open} transparent animationType="none" onRequestClose={() => setOpen(false)}>
        <TouchableOpacity style={sdStyles.backdrop} activeOpacity={1} onPress={() => setOpen(false)} />
        <View
          style={[
            sdStyles.menu,
            Platform.OS === 'web'
              ? ({ position: 'fixed', top: menuStyle.top, left: menuStyle.left, width: menuStyle.width } as any)
              : { position: 'absolute', top: menuStyle.top, left: menuStyle.left, width: menuStyle.width },
          ]}
        >
          <View style={sdStyles.searchBox}>
            <MaterialIcons name="search" size={16} color="#888" style={{ marginRight: 6 }} />
            <RNTextInput
              value={search}
              onChangeText={setSearch}
              placeholder="Search..."
              placeholderTextColor="#AAAAAA"
              style={sdStyles.searchInput}
              onStartShouldSetResponder={() => true}
            />
          </View>
          <FlatList
            data={filtered}
            keyExtractor={(item) => item.id}
            style={{ maxHeight: 280 }}
            keyboardShouldPersistTaps="always"
            renderItem={({ item }) => (
              <TouchableOpacity
                style={[sdStyles.option, selected?.id === item.id && sdStyles.optionSelected]}
                onPress={() => handleSelect(item)}
              >
                <Text style={sdStyles.optionText}>{item.displayText}</Text>
                {selected?.id === item.id && (
                  <MaterialIcons name="check" size={18} color="#0066CC" />
                )}
              </TouchableOpacity>
            )}
            ListEmptyComponent={<Text style={sdStyles.emptyText}>No results found</Text>}
          />
        </View>
      </Modal>
    </View>
  );
};

export default NhoSearchableDropdown;
